<template>
  <div v-if="props.sessions.length > 0" class="bg-white rounded-lg pb-5 px-6">
    <!-- Header with count -->
    <div class="flex items-center gap-3 mb-6">
      <div class="w-3 h-3 bg-red-500 rounded-full"></div>
      <h3 class="text-lg font-semibold text-gray-900">
        Inactive Sessions ({{ filteredSessions.length }})
      </h3>
    </div>

    <!-- No filtered results -->
    <div v-if="filteredSessions.length === 0" class="text-center py-8">
      <CalendarIcon class="mx-auto h-12 w-12 text-gray-400" />
      <h3 class="mt-2 text-sm font-medium text-gray-900">No inactive sessions match your search</h3>
      <p class="mt-1 text-sm text-gray-500">Try adjusting your search or filter criteria.</p>
    </div>

    <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div
        v-for="session in paginatedSessions"
        :key="session.id"
        class="bg-white border-2 border-black rounded-lg p-6 shadow-[4px_4px_0_0_rgba(0,0,0,1)] transition-all duration-300 hover:scale-102 cursor-pointer"
        @click="$emit('session-click', session)"
      >
        <!-- Session Header -->
        <div class="mb-4">
          <!-- Desktop: User Info + Status Tags in same row -->
          <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
            <!-- User Info -->
            <div class="flex items-center gap-3">
              <!-- Show UserAvatar only if user is not deactivated -->
              <UserAvatar
                v-if="!isUserDeactivated(session)"
                :src="getUserProfileImage(session)"
                :name="getUserName(session)"
                size="md"
              />
              <!-- Show placeholder for deactivated users -->
              <div
                v-else
                class="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center"
              >
                <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div class="flex-1">
                <h4 class="font-medium text-gray-900">{{ getUserName(session) }}</h4>
                <!-- Remove the duplicate tutor name display -->
              </div>
            </div>
            
            <!-- Status Tags - Desktop: upper right, Mobile: below -->
            <div class="flex items-center gap-2 flex-shrink-0">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 border-2 border-red-800 text-red-800 whitespace-nowrap"
              >
                {{ getStatusText(session.status) }}
              </span>
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                :class="getTypeClasses(session.type)"
              >
                {{ session.type === "face-to-face" ? "Face-to-Face" : "Online" }}
              </span>
            </div>
          </div>
        </div>

        <!-- Session Details -->
        <div class="space-y-3 mb-4">
          <div class="flex items-center gap-2 text-md text-gray-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            <span>{{ session.subject }}</span>
          </div>

          <div
            class="flex justify-between border-2 border-gray-200 bg-gray-50 rounded-lg py-2 px-4"
          >
            <div class="flex items-center gap-2 text-sm text-gray-700">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span class="font-semibold">{{ session.date }}</span>
            </div>

            <div class="flex items-center gap-2 text-sm text-gray-700">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span class="font-semibold">{{ session.time }}</span>
            </div>
          </div>

          <!-- Cancellation Info -->
          <div v-if="session.cancellationReason" class="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div class="text-sm text-gray-700">
              <span class="font-medium text-gray-500">Reason:</span> {{ formatCancellationReason(session.cancellationReason) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="bg-white rounded-lg mt-4 pb-3 sm:pb-5 px-3 sm:px-6">
      <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <div class="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
          Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ filteredSessions.length }} inactive sessions
        </div>
        <div class="flex items-center space-x-1 overflow-x-auto w-full sm:w-auto order-1 sm:order-2">
          <button
            @click="previousPage"
            :disabled="currentPage === 1"
            class="border-2 border-blue-800 rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1 flex-shrink-0"
            title="Previous page"
          >
            <ChevronLeftIcon class="w-3 h-3 sm:w-4 sm:h-4" />
            <span class="hidden sm:inline">Previous</span>
            <span class="sm:hidden">Prev</span>
          </button>
          <button
            v-for="page in totalPages"
            :key="page"
            @click="goToPage(page)"
            :class="[
              'px-2 sm:px-3 py-1 rounded-md border-2 border-blue-800 transition-colors min-w-[32px] sm:min-w-[40px] text-xs sm:text-sm flex-shrink-0',
              page === currentPage
                ? 'bg-blue-600 text-white font-semibold'
                : 'text-blue-700 hover:bg-blue-50',
            ]"
          >
            {{ page }}
          </button>
          <button
            @click="nextPage"
            :disabled="currentPage === totalPages"
            class="border-2 border-blue-800 rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1 flex-shrink-0"
            title="Next page"
          >
            <span class="hidden sm:inline">Next</span>
            <span class="sm:hidden">Next</span>
            <ChevronRightIcon class="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import UserAvatar from "@/components/UserAvatar.vue";
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/outline";

// Define the interface for cancelled sessions
interface CancelledSession {
  id: string;
  studentName?: string;
  tutorName?: string;
  studentProfileImage?: string | null;
  tutorProfileImage?: string | null;
  type: "face-to-face" | "online";
  status: "cancelled" | "declined";
  subject: string;
  date: string;
  time: string;
  location?: string;
  cancellationReason?: string;
  cancelledBy?: "student" | "tutor" | "admin" | "system";
  cancelledAt?: string;
  // Allow additional properties from the stores
  [key: string]: any;
}

interface Props {
  sessions: CancelledSession[];
  totalSessions: number;
  userType: "student" | "tutor"; // Determines which info to show (student sees tutor, tutor sees student)
  searchQuery?: string;
  subjectFilter?: string;
  itemsPerPage?: number;
}

const props = withDefaults(defineProps<Props>(), {
  sessions: () => [],
  totalSessions: 0,
  userType: "tutor",
  searchQuery: "",
  subjectFilter: "all",
  itemsPerPage: 4,
});

// Define emits
const emit = defineEmits<{
  "session-click": [session: CancelledSession];
}>();

// Pagination state
const currentPage = ref(1);

// Filter sessions based on search and filters
const filteredSessions = computed(() => {
  let filtered = props.sessions;

  // Apply search filter
  if (props.searchQuery) {
    filtered = filtered.filter(
      (session) =>
        (session.studentName?.toLowerCase().includes(props.searchQuery.toLowerCase()) ?? false) ||
        (session.tutorName?.toLowerCase().includes(props.searchQuery.toLowerCase()) ?? false) ||
        session.subject.toLowerCase().includes(props.searchQuery.toLowerCase())
    );
  }

  // Apply subject filter
  if (props.subjectFilter !== "all") {
    filtered = filtered.filter((session) =>
      session.subject.toLowerCase().includes(props.subjectFilter.toLowerCase())
    );
  }

  return filtered;
});

// Pagination calculations
const totalPages = computed(() => Math.ceil(filteredSessions.value.length / props.itemsPerPage));
const startIndex = computed(() => (currentPage.value - 1) * props.itemsPerPage);
const endIndex = computed(() =>
  Math.min(startIndex.value + props.itemsPerPage, filteredSessions.value.length)
);

// Get paginated sessions for current page
const paginatedSessions = computed(() => {
  return filteredSessions.value.slice(startIndex.value, endIndex.value);
});

// Pagination methods
const goToPage = (page: number) => {
  currentPage.value = page;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

// Helper functions
const getUserName = (session: CancelledSession): string => {
  return props.userType === "student" 
    ? (session.tutorName || "Unknown Tutor")
    : (session.studentName || "Unknown Student");
};

const getUserProfileImage = (session: CancelledSession): string | null | undefined => {
  return props.userType === "student" 
    ? session.tutorProfileImage
    : session.studentProfileImage;
};

const getTypeClasses = (type: string) => {
  switch (type) {
    case "face-to-face":
      return "bg-orange-100 text-orange-800 border-2 border-orange-800";
    case "online":
      return "bg-blue-100 text-blue-800 border-2 border-blue-800";
    default:
      return "bg-gray-100 text-gray-800 border-2 border-gray-800";
  }
};

const getStatusText = (status: string) => {
  switch (status) {
    case "cancelled":
      return "Cancelled";
    case "declined":
      return "Declined";
    default:
      return "Inactive";
  }
};

const isUserDeactivated = (session: CancelledSession): boolean => {
  const userName = getUserName(session);
  return userName === "Deactivated User";
};

const formatCancellationReason = (reason: string): string => {
  if (!reason) return "";
  
  // Convert kebab-case and snake_case to proper title case
  return reason
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};
</script>